/**
 * Rendering the contract's two text primitives.
 *
 * §2 of the API contract separates them deliberately, and so does this module:
 *
 *   `LocalizedString`   — the *server* authored it, so it arrives as a stable
 *                         machine `code` plus params. The frontend owns the
 *                         wording; `text` is an English reference only.
 *   `TranslatableText`  — a *person or provider* authored it (a ticket subject,
 *                         a knowledge snippet). It is never silently replaced:
 *                         when `translated` is true the original is always
 *                         carried alongside and the user can switch back to it.
 */

import { Icon, Languages } from "@norican/icons";
import { Badge, Button, cn, Typography } from "@norican/react";
import { type ComponentPropsWithoutRef, type ReactNode, useCallback, useState } from "react";

import { useT } from "@/i18n";
import { logger } from "@/lib/logger";

import type { LocalizedString, TranslatableText } from "./contract";

/**
 * How a server `code` maps onto this app's catalogues.
 *
 * The shell's namespaces already mirror the contract's enum sub-trees, so most
 * codes resolve unchanged inside one namespace. The handful that do not (the
 * ticket sub-trees drop their `ticket.` prefix) are rewritten here rather than
 * duplicating keys.
 */
const CODE_RULES: readonly { prefix: string; namespace: string; strip?: string }[] = [
  { prefix: "knowledge.source.", namespace: "assistant" },
  { prefix: "assistant.", namespace: "assistant" },
  { prefix: "ticket.status.", namespace: "tickets", strip: "ticket." },
  { prefix: "ticket.priority.", namespace: "tickets", strip: "ticket." },
  { prefix: "ticket.timeline.", namespace: "tickets", strip: "ticket." },
  { prefix: "ticket.approval.", namespace: "tickets", strip: "ticket." },
  { prefix: "error.", namespace: "errors" },
];

/**
 * Resolves a `LocalizedString` to display copy in the active language.
 *
 * Falls back to the server's English `text` when this build has no entry for
 * the code — which is exactly what should happen when the backend adds a code
 * the frontend catalogue has not caught up with yet. Showing the English line
 * is honest; showing a raw `action.effect.mailbox.grant_access` is not.
 */
export function useLocalizedText() {
  const { i18n } = useT();

  return useCallback(
    (value: LocalizedString | null | undefined): string => {
      if (value === null || value === undefined) {
        return "";
      }

      const rule = CODE_RULES.find((candidate) => value.code.startsWith(candidate.prefix));
      if (rule !== undefined) {
        const key = rule.strip === undefined ? value.code : value.code.slice(rule.strip.length);
        const fullKey = `${rule.namespace}:${key}`;
        if (i18n.exists(fullKey)) {
          // `t` is key-checked against the English catalogue, which cannot type
          // a key assembled at runtime. `exists()` on the line above is the
          // runtime equivalent of that check, so the signature is widened here
          // — in one place — rather than the whole hook being untyped.
          const translate = i18n.t as unknown as (
            key: string,
            options?: Record<string, string>,
          ) => string;
          // `params` is `dict[str, str]` by contract: always a safe bag.
          return translate(fullKey, value.params);
        }
      }

      logger.debug("[assistant] no catalogue entry for code", value.code);
      return value.text;
    },
    [i18n],
  );
}

export interface LocalizedTextProps extends ComponentPropsWithoutRef<"span"> {
  value: LocalizedString | null | undefined;
  /** Rendered when `value` is null — otherwise nothing is emitted. */
  fallback?: ReactNode;
}

/** A `LocalizedString`, translated by its code. */
export function LocalizedText({ value, fallback, ...props }: LocalizedTextProps) {
  const localize = useLocalizedText();
  const text = localize(value);
  if (text === "") {
    return fallback === undefined ? null : <span {...props}>{fallback}</span>;
  }
  return <span {...props}>{text}</span>;
}

export interface TranslatedTextProps extends Omit<ComponentPropsWithoutRef<"div">, "children"> {
  value: TranslatableText | null | undefined;
  /**
   * Suppress the "translated / show original" affordance. Only for places where
   * the text is one line in a dense list and the badge would dominate — the
   * affordance must still exist somewhere the user can reach.
   */
  hideAffordance?: boolean;
  /** Renders the resolved string. Defaults to a plain `body` paragraph. */
  render?: (text: string) => ReactNode;
}

/**
 * A `TranslatableText`, with the "this was machine translated" affordance.
 *
 * Two rules from the contract are enforced here rather than trusted to callers:
 * a machine translation is always labelled, and the original is always one
 * click away. Anything the user is about to *write back* must be composed from
 * `original_text`, never from what this renders.
 */
export function TranslatedText({
  value,
  hideAffordance = false,
  render,
  className,
  ...props
}: TranslatedTextProps) {
  const { t } = useT("assistant");
  const [showingOriginal, setShowingOriginal] = useState(false);

  if (value === null || value === undefined) {
    return null;
  }

  const isTranslated = value.translated && value.original_text !== null;
  const shown = isTranslated && showingOriginal ? (value.original_text as string) : value.text;
  const shownLang =
    isTranslated && showingOriginal ? (value.original_lang ?? value.lang) : value.lang;

  const body =
    render === undefined ? (
      <Typography variant="body" className="whitespace-pre-wrap">
        {shown}
      </Typography>
    ) : (
      render(shown)
    );

  if (!isTranslated || hideAffordance) {
    return (
      <div className={cn("flex flex-col gap-1", className)} lang={shownLang} {...props}>
        {body}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      <div lang={shownLang}>{body}</div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="info" variant="subtle" size="xs">
          {showingOriginal
            ? t("translated.original", { lang: value.original_lang ?? value.lang })
            : t("translated.badge")}
        </Badge>
        <Button
          variant="ghost"
          tone="neutral"
          size="sm"
          startIcon={<Icon icon={Languages} size="sm" />}
          onClick={() => setShowingOriginal((previous) => !previous)}
        >
          {showingOriginal ? t("translated.showTranslation") : t("translated.showOriginal")}
        </Button>
      </div>
    </div>
  );
}
